import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, AlertTriangle, PackageX, PhoneCall } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ReturnPolicy = () => {
  return (
    <div className="py-24 md:py-32 max-w-3xl mx-auto px-4 md:px-8" data-testid="return-policy-page">
      <Link to="/" data-testid="return-policy-back">
        <Button variant="ghost" className="rounded-full mb-6 -ml-3">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Home
        </Button>
      </Link>

      <h1
        className="text-4xl md:text-5xl font-light tracking-tight mb-3"
        style={{ fontFamily: 'Cormorant Garamond, serif' }}
      >
        Return &amp; Cancellation Policy
      </h1>
      <p className="text-sm text-muted-foreground mb-10">
        Please read carefully before placing your order.
      </p>

      {/* Highlight */}
      <div className="border-2 border-primary/30 bg-primary/5 rounded-2xl p-6 mb-10">
        <div className="flex items-start gap-3">
          <Clock className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-xl font-medium mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              24-hour Cancellation Window
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              You may cancel your order within <span className="font-medium text-foreground">24 hours</span>{' '}
              of placing it — full refund, no questions asked. After 24 hours, the order enters processing
              and cannot be cancelled.
            </p>
          </div>
        </div>
      </div>

      {/* No returns / exchanges */}
      <div className="border border-border rounded-2xl p-6 mb-10">
        <div className="flex items-start gap-3 mb-4">
          <PackageX className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-xl font-medium mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              No Returns · No Exchanges
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Every Bandhani Heritage piece is handcrafted and unique. Once dispatched, we do not accept
              returns or exchanges. Please review your order carefully before confirming payment.
            </p>
          </div>
        </div>

        <ul className="space-y-2 text-sm text-muted-foreground pl-9">
          <li className="flex gap-2"><span>•</span> All sales are final after the 24-hour window.</li>
          <li className="flex gap-2"><span>•</span> Slight variations in colour, print or weave are natural — they reflect the handmade craft.</li>
          <li className="flex gap-2"><span>•</span> We recommend saving colour and measurement questions on WhatsApp before ordering.</li>
        </ul>
      </div>

      {/* Damaged / wrong item */}
      <div className="border border-border rounded-2xl p-6 mb-10">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-xl font-medium mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Damaged or Incorrect Item on Arrival
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If your parcel arrives visibly damaged in transit or contains the wrong item, WhatsApp us
              <span className="font-medium text-foreground"> within 48 hours of delivery</span> with an
              unboxing video and photos. We will investigate with the courier and offer a resolution on a
              case-by-case basis. Requests raised after 48 hours cannot be entertained.
            </p>
          </div>
        </div>
      </div>

      {/* How to cancel */}
      <div className="border border-border rounded-2xl p-6 mb-10">
        <h2 className="text-xl font-medium mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          How to cancel within 24 hours
        </h2>
        <ol className="space-y-2 text-sm text-muted-foreground list-decimal pl-5">
          <li>WhatsApp us at <span className="font-medium text-foreground">+91 76210 03708</span> with your order number.</li>
          <li>Or email <span className="font-medium text-foreground">bandhaniheritage@gmail.com</span>.</li>
          <li>Once we confirm cancellation, refund is initiated to the original payment method within 5–7 working days.</li>
        </ol>
      </div>

      {/* Contact CTA */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <a
          href="https://wa.me/917621003708"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="return-policy-whatsapp"
          className="flex-1 w-full sm:w-auto"
        >
          <Button className="rounded-full w-full sm:w-auto">
            <PhoneCall className="h-4 w-4 mr-2" /> Reach us on WhatsApp
          </Button>
        </a>
        <p className="text-xs text-muted-foreground text-center sm:text-left">
          By placing an order you agree to this policy.
        </p>
      </div>
    </div>
  );
};

export default ReturnPolicy;
